import { IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { useGetCountriesQuery } from "generated/graphql";
import { type ReactNode, useState } from "react";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { CountryOption } from "src/features/create-accession/components/accession-data-form/wild-collection-dialog";

export const CountryPicker = ({
  value,
  onChange,
  placeholder: _placeholder,
  children,
}: {
  value: CountryOption | undefined | null;
  onChange: (val: CountryOption | undefined | null) => void;
  placeholder?: string;
  children?: ReactNode;
}) => {
  const isDesktop = useMediaQuery("md");
  const [open, setOpen] = useState(false);
  const placeholder = _placeholder ?? "Select country...";
  const [{ data, fetching }] = useGetCountriesQuery();
  const countries = data?.countries;

  const handleChange = (type: CountryOption) => {
    onChange(type.codeId === value?.codeId ? null : type);
    setOpen(false);
  };

  const commandContent = (
    <>
      <CommandEmpty>No results</CommandEmpty>
      <CommandList>
        {countries?.map((country) => (
          <CommandItem
            key={country.codeId}
            value={country.codeId}
            keywords={[country.name, country.codeId]}
            onSelect={() => {
              handleChange(country);
            }}
            selected={value?.codeId === country.codeId}
            data-cy="country-picker-item"
          >
            {country.name}
          </CommandItem>
        ))}
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild onFocusCapture={stopEventPropagation}>
          {children}
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-(--radix-popover-trigger-width)">
          <Command>
            <CommandInput placeholder={placeholder} fetching={fetching} />
            {commandContent}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
        {children}
      </DialogTrigger>
      <DialogContent className="p-0!">
        <Command size="lg">
          <CommandInput placeholder={placeholder} fetching={fetching}>
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandInput>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
